import { useMemo, useRef, useState, useEffect } from 'react';
import { Template } from '@/lib/templates';
import { renderTemplate, buildTemplatePreviewData, getAllTemplateMetadata } from '@shared/siteRenderer';

// Cache metadata lookup for supported features
const metadataMap = new Map(getAllTemplateMetadata().map(m => [m.id, m]));

interface TemplatePreviewProps {
  template: Template;
  selected?: boolean;
  onClick?: () => void;
  onPreview?: () => void;
  businessName?: string;
}

export function TemplatePreview({ template, selected, onClick, onPreview, businessName }: TemplatePreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const previewHtml = useMemo(() => {
    if (!isVisible) return '';
    const placeholderData = buildTemplatePreviewData(template.id);
    const meta = metadataMap.get(template.id);
    const features = meta?.defaultEnabledFeatures ?? [];
    return renderTemplate(template.id, placeholderData, features);
  }, [template.id, isVisible]);

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>
      <button
        onClick={onClick}
        style={{
          padding: 0,
          border: selected ? '3px solid #B08080' : '2px solid rgba(255,255,255,0.1)',
          borderRadius: '12px',
          cursor: 'pointer',
          overflow: 'hidden',
          background: 'rgba(255,255,255,0.02)',
          textAlign: 'left',
          transition: 'all 0.2s',
          width: '100%',
          flex: 1,
        }}
      >
        <div style={{
          aspectRatio: '4/3',
          position: 'relative',
          overflow: 'hidden',
          contain: 'strict',
          willChange: 'transform',
        }}>
          {isVisible ? (
            <iframe
              title={`${template.name} preview`}
              srcDoc={previewHtml}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '400%',
                height: '400%',
                border: 0,
                transform: 'scale(0.25)',
                transformOrigin: 'top left',
                pointerEvents: 'none',
              }}
              tabIndex={-1}
              sandbox=""
              loading="lazy"
            />
          ) : (
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(91,58,58,0.1), rgba(0,0,0,0.3))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: '2px solid rgba(176,128,128,0.3)',
                borderTopColor: '#B08080',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
            </div>
          )}
          {/* Click overlay */}
          <div style={{ position: 'absolute', inset: 0 }} />
          {selected && (
            <div style={{ position: 'absolute', top: '8px', right: '8px', width: '24px', height: '24px', borderRadius: '50%', background: '#B08080', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', zIndex: 10 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
          )}
        </div>
        <div style={{ padding: '12px', background: 'rgba(0,0,0,0.2)' }}>
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#FFFBFE', marginBottom: '2px' }}>{template.name}</p>
          <p style={{ fontSize: '11px', color: '#888888' }}>{template.category}{template.description ? ` \u00b7 ${template.description}` : ''}</p>
        </div>
      </button>
      {onPreview && businessName && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #B08080',
            background: 'rgba(176,128,128,0.1)',
            color: '#B08080',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(176,128,128,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(176,128,128,0.1)';
          }}
        >
          Full Preview
        </button>
      )}
    </div>
  );
}
